import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import React, { useContext } from 'react'

import {
  type ColorToken,
  CssVars,
  NeutralColorToken,
  SemanticColorToken,
} from '../../themes'
import { FoundationIcon } from '../FoundationIcon'
import type { Icon24 } from '../FoundationIcon/icons'
import { InternalElevate } from '../internal/InternalElevate'
import { NumberPog } from '../NumberPog'
import { Pog, type TextOrPictogram } from '../Pog'
import {
  getNeutralColorVars,
  getShadowColorVars,
  useThemeColorBase,
} from '../ThemeProvider'
import { InternalImage } from '../internal/InternalImage'

const styles = {
  iconWrapper: {
    // NOTE(omer.demirkan): To fix the line-height issue
    // on FoundationIcon
    display: 'flex',
  },
  mainContainer: css({
    flexGrow: 1,
    flexShrink: 1,
    minWidth: '120px',
  }),
  sectionRoot: css({
    '& > *': {
      padding: '0 8px',
    },
    '& > :first-of-type': {
      paddingLeft: '0',
    },
    '& > :last-of-type': {
      paddingRight: '0',
    },
    alignItems: 'center',
    display: 'flex',
    margin: '0',
    padding: '16px 0',
    position: 'relative',
  }),
}

// NOTE(omer.demirkan): Common pattern among Web Rows is
// to group items in the leading/trailing content by 8px
// instead of the usual 16px.
const contentGroupStyles = css({
  '& > *': {
    padding: '0 4px',
  },
  alignItems: 'center',
  display: 'flex',
  margin: '0 -4px',
})

type ContainerProps = {
  children?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLDivElement>
  overrideRootStyles?: CSSInterpolation
  isDisabled?: boolean
  /**
   * Determines if we want hover and active styles applied.
   */
  isInteractive?: boolean
  isElevated?: boolean
  isTabbable?: boolean
}

type SectionProps = {
  /**
   * Leading content: Supports list of content.
   */
  leadingContent?: React.ReactNode
  /**
   * Main content. This is what expands and shrinks
   * to accommodate container size.
   */
  mainContent: React.ReactNode
  /**
   * Trailing content. Supports list of content.
   */
  trailingContent?: React.ReactNode
  divider?: 'bottom' | 'top' | 'both' | false | undefined
  onClick?: () => any
  overrideRootStyles?: CSSInterpolation
  align?: 'center' | 'top'
}

// NOTE(omer.demirkan): To be able to compose props in rows
// to only accept certain types of leading content
export type StartElemIconProps = {
  startElem: 'icon'
  startIcon: Icon24
  startIconColor?: ColorToken | null
}
export type StartElemPogProps = {
  startElem: 'pog'
  startPog: TextOrPictogram
}
export type StartElemNumberPogProps = {
  startElem: 'numberPog'
  startNumber: number
}
export type StartElemCheckmarkProps = {
  startElem: 'checkmark'
}
export type StartElemImageProps = {
  startElem: 'image'
  startImageSrc: string
  startImageWidth?: number
  startImageHeight?: number
}
export type StartElemNoneProps = {
  startElem?: 'none'
}
export type StartElemProps =
  | StartElemIconProps
  | StartElemPogProps
  | StartElemNumberPogProps
  | StartElemCheckmarkProps
  | StartElemImageProps
  | StartElemNoneProps

export type AnnotationProps =
  | {
      annotation?: never
      annotationTitle?: never
    }
  | {
      // NOTE(omer.demirkan): Annotation is a react fragment to allow
      // for inline links.
      /**
       * When given, an icon will appear next to
       * the label that triggers a popover.
       * This is the content of that popover.
       */
      annotation: React.ReactNode
      /**
       * To determine the title of the helper popover.
       */
      annotationTitle?: string
    }

type ColorCategories =
  | 'icon'
  | 'primaryText'
  | 'secondaryText'
  | 'caret'
  | 'divider'
  | 'success'

type RowContextState = {
  isDisabled: boolean
}

export const RowUtils = {
  Container,
  Icon,
  LeadingContent,
  Section,
  contentGroupStyles,
  useRowColors,
}

const RowContext = React.createContext<RowContextState>({ isDisabled: false })

const neutralColorVars = getNeutralColorVars()
const shadowColorVars = getShadowColorVars()

/**
 * A generic, reusable container component with hover,
 * clicked, isDisabled, padding, border radius out of the box.
 */
function Container({
  children,
  isDisabled = false,
  isElevated = false,
  isInteractive = false,
  isTabbable = false,
  onClick,
  overrideRootStyles,
}: ContainerProps) {
  const elevatedBorderColor = useThemeColorBase(NeutralColorToken.Bg3)
  const defaultBackgroundColor = useThemeColorBase(NeutralColorToken.Bg1)

  const containerRef = React.useRef<HTMLDivElement>(null)

  const handleClick = !isDisabled ? onClick : undefined
  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> =
    React.useCallback(
      e => {
        if (e.key === 'Enter' && handleClick) {
          containerRef.current?.click()
        }
      },
      [handleClick]
    )

  return (
    <RowContext.Provider value={{ isDisabled }}>
      <InternalElevate amount={isElevated ? 1 : 0}>
        {({ rootClassName }) => (
          <div
            ref={containerRef}
            aria-disabled={isDisabled}
            className={rootClassName}
            css={[
              {
                border: '1px solid transparent',
                borderBottomWidth: '0',
                borderRadius: '4px',
                margin: '0 -24px',
                padding: '0 23px',
              },
              !isDisabled &&
                isInteractive && {
                  ':hover': {
                    backgroundColor: neutralColorVars.hoverBg1,
                  },
                  cursor: 'pointer',
                },
              !isDisabled &&
                isInteractive && {
                  ':active': {
                    backgroundColor: neutralColorVars.pressedBg1,
                  },
                },
              !isDisabled &&
                isInteractive && {
                  ':focus-visible': {
                    backgroundColor: defaultBackgroundColor,
                  },
                },
              !isDisabled &&
                isElevated && {
                  backgroundColor: neutralColorVars.bg1,
                  border: `1px solid ${elevatedBorderColor}`,
                  boxShadow: `0px 12px 24px ${shadowColorVars.s1}, 0px -12px 24px ${shadowColorVars.s2}`,
                },
              isDisabled && {
                cursor: 'auto',
              },
              overrideRootStyles,
            ]}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={isTabbable ? 0 : undefined}
          >
            {children}
          </div>
        )}
      </InternalElevate>
    </RowContext.Provider>
  )
}

/**
 * A generic horizontal section of a Web Row.
 *
 * Consists of one section that grows and shrinks to accommodate
 * container size, and any number of sections before and after it.
 * Appropriate margins for contents are set internally.
 */
function Section({
  align,
  divider,
  leadingContent,
  mainContent,
  overrideRootStyles,
  trailingContent,
}: SectionProps) {
  return (
    <div
      css={[
        styles.sectionRoot,
        {
          alignItems: align === 'top' ? 'start' : 'center',
        },
        (divider === 'bottom' || divider === 'both') && {
          borderBottom: `1px solid ${CssVars.neutral.bg3}`,
        },
        (divider === 'top' || divider === 'both') && {
          borderTop: `1px solid ${CssVars.neutral.bg3}`,
        },
        overrideRootStyles,
      ]}
    >
      {leadingContent}
      <div css={styles.mainContainer}>{mainContent}</div>
      {trailingContent}
    </div>
  )
}

const enabledColorTokenByRole: {
  [key in ColorCategories]: NeutralColorToken | SemanticColorToken
} = {
  caret: NeutralColorToken.Fg2,
  divider: NeutralColorToken.Bg3,
  icon: NeutralColorToken.Fg1,
  primaryText: NeutralColorToken.Fg1,
  secondaryText: NeutralColorToken.Fg2,
  success: SemanticColorToken.Success,
}
const disabledColorTokenByRole: {
  [key in ColorCategories]: NeutralColorToken | SemanticColorToken
} = {
  caret: NeutralColorToken.Fg3,
  divider: NeutralColorToken.Bg3,
  icon: NeutralColorToken.Fg3,
  primaryText: NeutralColorToken.Fg3,
  secondaryText: NeutralColorToken.Fg3,
  success: NeutralColorToken.Fg3,
}

/**
 * A hook to provide proper color tokens for different types of content.
 */
function useRowColors(isDisabled?: boolean) {
  const contextState = useContext(RowContext)
  const disabledState =
    typeof isDisabled !== 'boolean' ? contextState.isDisabled : isDisabled

  return {
    colors: disabledState ? disabledColorTokenByRole : enabledColorTokenByRole,
  }
}

// NOTE(omer.demirkan): Passing in a color to FoundationIcon
// has the effect of adding 4px to the height due to line-height
// (from TextColorOverride?)
// This is to avoid putting <div css={{display: 'flex'}}> everywhere
// there is an icon.
function Icon(props: React.ComponentProps<typeof FoundationIcon>): React.JSX.Element {
  return (
    // aria-hidden since we want screenreaders to only dig into
    // number-pogs for rows.
    <div aria-hidden="true" css={{ display: 'flex' }}>
      <FoundationIcon {...props} />
    </div>
  )
}

function LeadingContent(props: StartElemProps & { isDisabled?: boolean }) {
  const { colors } = useRowColors(props.isDisabled)

  // NOTE(omer.demirkan): divs are so that child padding styles
  // don't interfere with pogs.
  switch (props.startElem) {
    case 'icon':
      return (
        <Icon
          color={props.startIconColor ?? colors.icon}
          type={props.startIcon}
        />
      )
    case 'pog':
      return (
        <div aria-hidden="true">
          <Pog isDisabled={props.isDisabled} {...props.startPog} />
        </div>
      )
    case 'numberPog':
      return (
        <div>
          <NumberPog isDisabled={props.isDisabled} number={props.startNumber} />
        </div>
      )
    case 'checkmark':
      return <Icon color={colors.success} type="Checkmark24" />
    case 'image':
      return (
      <div>
        <InternalImage src={props.startImageSrc} width={props.startImageWidth ?? 40} height={props.startImageHeight ?? 40} />
      </div>
      );
    case 'none':
    default:
      return null
  }
}
